import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Menu, MenuItem, useMediaQuery, useTheme } from '@mui/material'
import React, { useState, useEffect } from 'react'
import { colors } from 'theme'
import * as Styled from '../../features/order-managment/OrderManagementStyles'

export interface FilterOption {
  label: string
  value: string
  isAllOption?: boolean
}

export interface DropdownFilterProps {
  label: string
  selectedOptions: FilterOption[]
  onValueChange: (values: FilterOption[]) => void
  options: FilterOption[]
  width?: number
  multiple?: boolean
}

export const DropdownFilter: React.FC<DropdownFilterProps> = ({
  label,
  selectedOptions,
  onValueChange,
  options,
  width = 220,
  multiple = false
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // Set "All" option as selected by default
  useEffect(() => {
    if (selectedOptions.length === 0) {
      const allOption = options.find(opt => opt.isAllOption)
      if (allOption) {
        onValueChange([allOption])
      }
    }
  }, []) // Empty dependency array means this runs once on mount

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleMenuItemClick = (option: FilterOption) => {
    if (multiple) {
      if (option.isAllOption) {
        // If "All" option is clicked, unselect all other options
        onValueChange([option])
      } else {
        // If a regular option is clicked
        const isSelected = selectedOptions.some(selected => selected.value === option.value)
        const newSelectedOptions = isSelected
          ? selectedOptions.filter(selected => selected.value !== option.value)
          : [...selectedOptions.filter(selected => !selected.isAllOption), option]
        
        // If no options are selected, select the "All" option
        if (newSelectedOptions.length === 0) {
          const allOption = options.find(opt => opt.isAllOption)
          if (allOption) {
            onValueChange([allOption])
          }
        } else {
          // Ensure "All" option is not in the selection when other options are selected
          const filteredOptions = newSelectedOptions.filter(selected => !selected.isAllOption)
          onValueChange(filteredOptions)
        }
      }
    } else {
      onValueChange([option])
      handleClose()
    }
  }

  const getDisplayText = () => {
    if (selectedOptions.length === 0) return 'None'
    if (selectedOptions.length === 1) return selectedOptions[0].label
    return `${selectedOptions.length} selected`
  }

  // Sort options to put the "All" option first
  const sortedOptions = [...options].sort((a, b) => {
    if (a.isAllOption) return -1
    if (b.isAllOption) return 1
    return 0
  })

  return (
    <>
      <Styled.OrderTypeDropdownMenu
        id={`${label.toLowerCase()}-filter-button`}
        aria-controls={open ? `${label.toLowerCase()}-menu` : undefined}
        aria-haspopup='true'
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        style={{ width: isMobile ? '100%' : `${width}px` }}
      >
        <span style={{ textWrap: 'nowrap', overflow: 'hidden' }}>
          {label}:
          <Styled.SelectedOrderType>
            {getDisplayText()}
          </Styled.SelectedOrderType>
        </span>
        <div style={{ marginLeft: '5px', marginTop: '5px' }}>
          <ExpandMoreIcon />
        </div>
      </Styled.OrderTypeDropdownMenu>
      <Menu
        id={`${label.toLowerCase()}-menu`}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': `${label.toLowerCase()}-filter-button`,
        }}
      >
        {sortedOptions.map((option) => {
          const isSelected = selectedOptions.some(selected => selected.value === option.value)
          return (
            <MenuItem
              key={option.value}
              onClick={(e) => {
                e.preventDefault()
                handleMenuItemClick(option)
              }}
              sx={{
                backgroundColor: isSelected ? colors.gray[150] : '#fff',
              }}
            >
              <Styled.CheckboxWrapper>
                <Styled.Checkbox type='checkbox' checked={isSelected} readOnly />
              </Styled.CheckboxWrapper>
              <span style={{ width: isMobile ? '100%' : `${width}px`, textWrap: 'nowrap' }}>{option.label}</span>
            </MenuItem>
          )
        })}
      </Menu>
    </>
  )
} 